
import { mixins, Options } from 'vue-class-component'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { Prop, Watch } from 'vue-property-decorator'
import { IForm, IInitialCampaign, IPushCampaign, IApp } from '@/utils/types'
import { ACTION_FORM, ACTION_APP } from '@/store/actions'
import cloneDeep from 'lodash/cloneDeep'
import TagInputContent from '@/components/common/ui/TagInputContent.vue'
import InputEmojiSelector from '@/components/common/ui/InputEmojiSelector.vue'
import FormDetail from '@/components/form/FormDetail.vue'
import { ASSETS_FORM_TYPE } from '@/utils/constants'
import SendingTestModal from '@/components/common/ui/SendingTestModal.vue'
import FormPreview from '@/components/common/ui/FormPreview.vue'
import ChatPreview from '@/components/common/ui/ChatPreview.vue'

@Options({
  components: { FormDetail, InputEmojiSelector, TagInputContent, SendingTestModal, FormPreview, ChatPreview },
  emits: ['update:onSelect', 'update:onCreate'],
})
export default class FormFormModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  formConfig!: IForm

  @Prop()
  campaign!: IInitialCampaign | IPushCampaign

  @Prop({ default: '' })
  appId!: string

  form: IForm = {
    _id: '',
    title: '',
    app_id: this.appId,
    message: '',
    details: [],
    liff_details: [],
    liff_id: '',
    applied_campain: 0,
    type: ASSETS_FORM_TYPE.CHAT,
    form_size: 'compact',
    liff_id_test: '',
  }

  selectingApp: IApp = {}
  sendingTestModalVisible = false
  chatPreviewData: Record<string, any> = {}
  isFormInitialized = false
  isUpdatingFromFormDetail = false

  get requiredRule() {
    return {
      formName: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.form.form_name'),
      }),
    }
  }

  get liffUrl() {
    return this.form.liff_id ? `https://liff.line.me/${this.form.liff_id}` : ''
  }

  get liffUrlTestMode() {
    return this.form.liff_id_test ? `https://liff.line.me/${this.form.liff_id_test}` : ''
  }

  get modalTitle() {
    return this.isNew ? this.$t('label.form.add_new_form') : this.$t('label.form.edit_form')
  }

  handleSendingTestModal() {
    this.sendingTestModalVisible = true
  }

  onCloseModal() {
    this.sendingTestModalVisible = false
  }

  onUpdateFormSize(value) {
    this.form.form_size = value
  }

  onUpdateFormImageHeaderUrl(value) {
    this.form.image_header_url = value
  }

  onFormDetailUpdate(newData: any[]) {
    this.isUpdatingFromFormDetail = true
    this.form.liff_details = [...newData]
    this.$nextTick(() => {
      this.isUpdatingFromFormDetail = false
    })
  }

  @Watch('form.details', { deep: true })
  onDetailsChange() {
    this.chatPreviewData = this.initializeChatPreviewData(this.chatPreviewData)
  }

  initializeChatPreviewData(existingData: Record<string, any> = {}) {
    if (!this.form.details || this.form.details.length === 0) {
      return {}
    }

    // Initialize chat preview data, preserving existing
    return this.form.details.reduce((data, field) => {
      if (field.field_name) {
        if (existingData[field.field_name] !== undefined) {
          data[field.field_name] = existingData[field.field_name]
        } else {
          data[field.field_name] = ''
        }
      }
      return data
    }, {} as Record<string, any>)
  }

  onSubmit() {
    this.$refs.formRef
      .validate()
      .then(async (success: boolean) => {
        if (!success) {
          return
        }

        if (
          this.form.type === ASSETS_FORM_TYPE.LIFF &&
          (this.selectingApp.liff_channel_id === '' || this.selectingApp.liff_channel_secret === '')
        ) {
          this.$q.notify({
            type: 'negative',
            message: this.$t('messages.please_set_liff_channel_id_in_app_settings'),
          })
          return
        }

        await this.doSave()
      })
      .catch((error: unknown) => {
        console.log('error', error)
      })
  }

  async doSave() {
    const { dispatch } = this.$store
    let form = null
    const isNew = this.isNew
    if (isNew) {
      form = await dispatch(ACTION_FORM.ADD_NEW, {
        ...this.form,
      })
    } else {
      form = await dispatch(ACTION_FORM.UPDATE, {
        _id: this.formConfig._id,
        ...this.form,
      })
    }

    if (form) {
      if (isNew) {
        this.$emit('update:onCreate', form)
      } else {
        this.$emit('update:onSelect', form)
      }
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }

    this.visible = false
  }

  mounted() {
    this.$nextTick(() => {
      this.$refs.formRef.validate()
      this.$refs.refTitle.focus()
    })
  }

  async activateLIFF() {
    if (!this.formConfig._id) {
      return
    }

    const { dispatch } = this.$store
    const form = await dispatch(ACTION_FORM.ACTIVATE_LIFF, {
      _id: this.formConfig._id,
      ...this.form,
    })
    if (form) {
      this.$emit('update:onSelect', form)
      this.$q.notify({
        message: this.$t('messages.saved'),
        color: 'positive',
      })
    }
    this.visible = false
  }

  async created() {
    this.selectingApp = await this.$store.dispatch(ACTION_APP.LOAD_ITEM, this.formConfig.app_id)
    this.form = { ...this.form, ...cloneDeep(this.formConfig) }
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm)
    this.chatPreviewData = this.initializeChatPreviewData()

    // Set initialization flag after form is populated
    this.$nextTick(() => {
      this.isFormInitialized = true
    })
  }
}
